import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Fragment, useState, type ReactElement } from "react";
import { Field, FormPage, FormSection, MoneyInput, QtyInput, QueryStatus, useToast } from "@cx/ui";
import type { ServiceOrderFormOptions, ServiceOrderView } from "@cx/contracts";
import { formatBRL, todayInSaoPaulo } from "@cx/domain";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { ProductSearch } from "../prices/ProductSearch";
import { ContactPicker } from "../sales-orders/SalesOrderFormPage";
import {
  emptyServiceOrderForm,
  formTotals,
  hoursLabel,
  osItemError,
  osRowKey,
  serviceOrderBody,
  serviceOrderFormFromView,
  type OsItemRow,
  type ServiceOrderForm,
} from "./labels";

/** Inclusão (/servicos/ordens-de-servico/nova) e edição (/servicos/ordens-de-servico/$id/editar) da OS. */
export function ServiceOrderFormPage(): ReactElement {
  const params = useParams({ strict: false }) as { id?: string };
  const id = params.id;
  const { companyId, api } = useSession();
  const options = useQuery({
    queryKey: ["service-orders", companyId, "form-options"],
    queryFn: () => api<ServiceOrderFormOptions>("/service-orders/form-options"),
  });
  const order = useQuery({
    queryKey: ["service-orders", companyId, "view", id],
    enabled: !!id,
    refetchOnMount: "always",
    queryFn: () => api<ServiceOrderView>(`/service-orders/${id}`),
  });
  if (!options.data || (id && !order.data))
    return (
      <QueryStatus loading={options.isLoading || order.isLoading} error={options.error ?? order.error} />
    );
  if (order.data?.lockedReason)
    return (
      <div className="alert alert-warning" role="alert">
        {order.data.lockedReason}
      </div>
    );
  return <OsFormBody key={id ?? "new"} options={options.data} order={order.data ?? null} />;
}

function OsFormBody({
  options,
  order,
}: {
  options: ServiceOrderFormOptions;
  order: ServiceOrderView | null;
}) {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const onlySeller = options.sellerLocked ? (options.sellers[0]?.id ?? "") : "";
  const [f, setF] = useState<ServiceOrderForm>(() =>
    order ? serviceOrderFormFromView(order) : emptyServiceOrderForm(todayInSaoPaulo(), onlySeller),
  );
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Uma chave por tela de inclusão: clique duplo ou repetição da rede não criam duas OS.
  const [idemKey] = useState(() => crypto.randomUUID());
  const set = <K extends keyof ServiceOrderForm>(k: K, v: ServiceOrderForm[K]) => {
    setF((x) => ({ ...x, [k]: v }));
    setDirty(true);
  };
  const setItem = (key: string, patch: Partial<OsItemRow>) => {
    setF((x) => ({ ...x, items: x.items.map((i) => (i.key === key ? { ...i, ...patch } : i)) }));
    setDirty(true);
  };
  const remove = (key: string) =>
    set(
      "items",
      f.items.filter((i) => i.key !== key),
    );
  const addService = () =>
    set("items", [
      ...f.items,
      {
        key: osRowKey(),
        kind: "service",
        productId: null,
        description: "",
        code: "",
        unit: "h",
        qtyQ4: 10_000,
        unitPriceCents: null,
        toQuote: false,
        done: false,
      },
    ]);

  const { totals, error: totalsError } = formTotals(f);

  const save = useMutation({
    mutationFn: async () => {
      const body = JSON.stringify(serviceOrderBody(f));
      if (order) {
        await api(`/service-orders/${order.id}`, { method: "PUT", body });
        return order.id;
      }
      const r = await api<{ id: string }>("/service-orders", {
        method: "POST",
        body,
        headers: { "Idempotency-Key": idemKey },
      });
      return r.id;
    },
    onSuccess: (savedId) => {
      toast("success", "Ordem de serviço salva");
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["service-orders", companyId] });
      navigate({ to: "/servicos/ordens-de-servico/$id", params: { id: savedId } });
    },
    onError: (e) => {
      setErrors(e instanceof ApiFailure && e.fields ? e.fields : {});
      if (e instanceof ApiFailure && e.code === "invalid_contact") setErrors({ contactId: e.message });
      toast("danger", errorMessage(e));
    },
  });

  const title = order ? `Ordem de serviço nº ${order.number}` : "Nova ordem de serviço";
  const back = () =>
    order
      ? navigate({ to: "/servicos/ordens-de-servico/$id", params: { id: order.id } })
      : navigate({ to: "/servicos/ordens-de-servico" });
  const services = f.items.map((it, index) => ({ it, index })).filter((x) => x.it.kind === "service");
  const parts = f.items.map((it, index) => ({ it, index })).filter((x) => x.it.kind === "part");
  const select = (
    k: "sellerId" | "storeId" | "paymentTermId" | "paymentMethodId" | "financialCategoryId" | "warehouseId",
    list: { id: string; name: string }[],
    empty: string | null,
    current: { id: string; name: string } | null | undefined,
  ) => (
    <select className="form-select" value={f[k]} onChange={(e) => set(k, e.target.value)}>
      {empty !== null && <option value="">{empty}</option>}
      {current && !list.some((x) => x.id === current.id) && (
        <option value={current.id}>{current.name}</option>
      )}
      {list.map((x) => (
        <option key={x.id} value={x.id}>
          {x.name}
        </option>
      ))}
    </select>
  );

  return (
    <FormPage
      title={title}
      breadcrumb={["Serviços", "Ordens de serviço", order ? String(order.number) : "Nova"]}
      onSave={() => save.mutate()}
      onCancel={back}
      saving={save.isPending}
      dirty={dirty}
      aside={
        <div className="position-sticky" style={{ top: "1rem" }}>
          <div className="box card p-3 mb-3" aria-live="polite">
            <h2 className="h6">Totais</h2>
            {totalsError && <div className="alert alert-danger py-2 small">{totalsError}</div>}
            {!totals && !totalsError && (
              <p className="text-muted small mb-0">Informe quantidade e preço dos itens.</p>
            )}
            {totals && (
              <dl className="row mb-0 small">
                {(
                  [
                    [`Serviços (${hoursLabel(totals.hoursSumQ4)})`, totals.servicesTotalCents],
                    ["Peças", totals.partsTotalCents],
                    ["Frete", totals.freightCents],
                    ["Desconto", -totals.discountCents],
                  ] as const
                ).map(([label, v]) => (
                  <Fragment key={label}>
                    <dt className="col-7 fw-normal">{label}</dt>
                    <dd className="col-5 text-end mb-1">{formatBRL(v)}</dd>
                  </Fragment>
                ))}
                <dt className="col-7">Total da OS</dt>
                <dd className="col-5 text-end fw-bold mb-1" data-testid="os-total">
                  {formatBRL(totals.totalCents)}
                </dd>
              </dl>
            )}
            {f.items.some((i) => i.kind === "part" && i.unitPriceCents === null) && (
              <p className="small text-muted mt-2 mb-0">Peça sem preço: vale o preço de venda do produto.</p>
            )}
          </div>
        </div>
      }
    >
      <FormSection title="Cliente e equipamento">
        <div className="row">
          <div className="col-md-8 mb-3">
            <ContactPicker value={f.contact} error={errors.contactId} onChange={(c) => set("contact", c)} />
          </div>
          <Field label="Vendedor" className="col-md-4" error={errors.sellerId}>
            {options.sellerLocked ? (
              <select className="form-select" value={f.sellerId} disabled>
                {options.sellers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            ) : (
              select("sellerId", options.sellers, "Sem vendedor", order?.seller)
            )}
          </Field>
          <Field label="Equipamento" className="col-md-6" error={errors.equipment}>
            <input value={f.equipment} maxLength={200} onChange={(e) => set("equipment", e.target.value)} />
          </Field>
          <Field label="Nº de série" className="col-md-3" error={errors.serialNumber}>
            <input
              value={f.serialNumber}
              maxLength={60}
              onChange={(e) => set("serialNumber", e.target.value)}
            />
          </Field>
          <Field label="Técnico" className="col-md-3" error={errors.technician}>
            <input value={f.technician} maxLength={120} onChange={(e) => set("technician", e.target.value)} />
          </Field>
          <Field label="Problema relatado" className="col-12" error={errors.problem}>
            <textarea
              rows={2}
              maxLength={5000}
              value={f.problem}
              onChange={(e) => set("problem", e.target.value)}
            />
          </Field>
          <Field
            label="Observações do recebimento"
            className="col-12"
            hint="Estado do equipamento e acessórios; saem no comprovante de entrada."
            error={errors.receptionNotes}
          >
            <textarea
              rows={2}
              maxLength={5000}
              value={f.receptionNotes}
              onChange={(e) => set("receptionNotes", e.target.value)}
            />
          </Field>
        </div>
      </FormSection>

      <FormSection title="Datas">
        <div className="row">
          <Field label="Data de entrada" required className="col-md-3" error={errors.entryDate}>
            <input type="date" value={f.entryDate} onChange={(e) => set("entryDate", e.target.value)} />
          </Field>
          <Field label="Hora de início" className="col-md-3" error={errors.startTime}>
            <input type="time" value={f.startTime} onChange={(e) => set("startTime", e.target.value)} />
          </Field>
          <Field label="Data prevista" className="col-md-3" error={errors.expectedDate}>
            <input type="date" value={f.expectedDate} onChange={(e) => set("expectedDate", e.target.value)} />
          </Field>
          <Field label="Loja" className="col-md-3" error={errors.storeId}>
            {select("storeId", options.stores, "Nenhuma", order?.store)}
          </Field>
          <Field
            label="Data de término"
            className="col-md-3"
            hint="Vazio: preenchida ao finalizar."
            error={errors.finishDate}
          >
            <input type="date" value={f.finishDate} onChange={(e) => set("finishDate", e.target.value)} />
          </Field>
          <Field label="Hora de término" className="col-md-3" error={errors.endTime}>
            <input type="time" value={f.endTime} onChange={(e) => set("endTime", e.target.value)} />
          </Field>
          <Field label="Data de saída" className="col-md-3" error={errors.exitDate}>
            <input type="date" value={f.exitDate} onChange={(e) => set("exitDate", e.target.value)} />
          </Field>
        </div>
      </FormSection>

      <FormSection title="Serviços">
        {errors.items && <div className="alert alert-danger py-2">{errors.items}</div>}
        {services.length === 0 ? (
          <p className="text-muted">Nenhum serviço.</p>
        ) : (
          <div className="table-responsive mb-3">
            <table className="table align-middle" aria-label="Serviços da OS">
              <thead>
                <tr>
                  <th scope="col">Descrição</th>
                  <th scope="col" style={{ width: "7rem" }}>
                    Código
                  </th>
                  <th scope="col" style={{ width: "7rem" }} className="text-end">
                    Horas
                  </th>
                  <th scope="col" style={{ width: "9rem" }} className="text-end">
                    Preço/hora
                  </th>
                  <th scope="col">Orçar</th>
                  <th scope="col">OK</th>
                  <th scope="col" className="text-end">
                    Total
                  </th>
                  <th scope="col">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {services.map(({ it, index }, n) => {
                  const err = osItemError(errors, index);
                  const name = it.description || `serviço ${n + 1}`;
                  return (
                    <tr key={it.key}>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          aria-label={`Descrição do serviço ${n + 1}`}
                          maxLength={1000}
                          value={it.description}
                          onChange={(e) => setItem(it.key, { description: e.target.value })}
                        />
                        {err && <div className="invalid-feedback d-block">{err}</div>}
                      </td>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          aria-label={`Código do serviço ${n + 1}`}
                          maxLength={60}
                          value={it.code}
                          onChange={(e) => setItem(it.key, { code: e.target.value })}
                        />
                      </td>
                      <td>
                        <QtyInput
                          aria-label={`Horas de ${name}`}
                          className="form-control form-control-sm text-end"
                          value={it.qtyQ4}
                          onChange={(v) => setItem(it.key, { qtyQ4: v })}
                        />
                      </td>
                      <td>
                        <MoneyInput
                          aria-label={`Preço por hora de ${name}`}
                          className="form-control form-control-sm text-end"
                          value={it.unitPriceCents}
                          onChange={(v) => setItem(it.key, { unitPriceCents: v })}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          aria-label={`Orçar ${name}`}
                          checked={it.toQuote}
                          onChange={(e) => setItem(it.key, { toQuote: e.target.checked })}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          aria-label={`${name} executado`}
                          checked={it.done}
                          onChange={(e) => setItem(it.key, { done: e.target.checked })}
                        />
                      </td>
                      <td className="text-end text-nowrap">
                        {totals ? formatBRL(totals.lines[index]!.totalCents) : "—"}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          aria-label={`Remover ${name}`}
                          onClick={() => remove(it.key)}
                        >
                          Remover
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <button type="button" className="btn btn-outline-secondary mb-3" onClick={addService}>
          Adicionar serviço
        </button>
      </FormSection>

      <FormSection title="Peças">
        {parts.length === 0 ? (
          <p className="text-muted">Nenhuma peça. Busque um produto abaixo.</p>
        ) : (
          <div className="table-responsive mb-3">
            <table className="table align-middle" aria-label="Peças da OS">
              <thead>
                <tr>
                  <th scope="col">Produto</th>
                  <th scope="col" style={{ width: "5rem" }}>
                    Un
                  </th>
                  <th scope="col" style={{ width: "7rem" }} className="text-end">
                    Qtd
                  </th>
                  <th scope="col" style={{ width: "9rem" }} className="text-end">
                    Preço un.
                  </th>
                  <th scope="col" className="text-end">
                    Total
                  </th>
                  <th scope="col">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {parts.map(({ it, index }) => {
                  const err = osItemError(errors, index);
                  return (
                    <tr key={it.key}>
                      <td>
                        {it.description}
                        {it.code && <span className="text-muted small ms-2">{it.code}</span>}
                        {err && <div className="invalid-feedback d-block">{err}</div>}
                      </td>
                      <td>{it.unit || "—"}</td>
                      <td>
                        <QtyInput
                          aria-label={`Quantidade de ${it.description}`}
                          className="form-control form-control-sm text-end"
                          value={it.qtyQ4}
                          onChange={(v) => setItem(it.key, { qtyQ4: v })}
                        />
                      </td>
                      <td>
                        <MoneyInput
                          aria-label={`Preço unitário de ${it.description}`}
                          className="form-control form-control-sm text-end"
                          value={it.unitPriceCents}
                          onChange={(v) => setItem(it.key, { unitPriceCents: v })}
                        />
                      </td>
                      <td className="text-end text-nowrap">
                        {totals ? formatBRL(totals.lines[index]!.totalCents) : "—"}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          aria-label={`Remover ${it.description}`}
                          onClick={() => remove(it.key)}
                        >
                          Remover
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="row">
          <div className="col-md-8">
            <ProductSearch
              id="os-product"
              label="Adicionar peça"
              action="Adicionar"
              onPick={(p) =>
                set("items", [
                  ...f.items,
                  {
                    key: osRowKey(),
                    kind: "part",
                    productId: p.id,
                    description: p.name,
                    code: p.sku ?? "",
                    unit: p.unit ?? "",
                    qtyQ4: 10_000,
                    unitPriceCents: p.priceCents,
                    toQuote: false,
                    done: false,
                  },
                ])
              }
            />
          </div>
          <Field label="Depósito das peças" className="col-md-4" error={errors.warehouseId}>
            {select("warehouseId", options.warehouses, "Padrão da empresa", order?.warehouse)}
          </Field>
        </div>
      </FormSection>

      <FormSection title="Valores e pagamento">
        <div className="row">
          <Field label="Frete" className="col-md-3" error={errors.freightCents}>
            <MoneyInput value={f.freightCents} onChange={(v) => set("freightCents", v)} />
          </Field>
          <Field label="Desconto" className="col-md-3" error={errors.discountCents}>
            <MoneyInput value={f.discountCents} onChange={(v) => set("discountCents", v)} />
          </Field>
          <Field label="Condição de pagamento" className="col-md-6" error={errors.paymentTermId}>
            {select("paymentTermId", options.paymentTerms, "À vista (vence no término)", order?.paymentTerm)}
          </Field>
          <Field label="Forma de pagamento" className="col-md-6" error={errors.paymentMethodId}>
            {select("paymentMethodId", options.paymentMethods, "Não informada", order?.paymentMethod)}
          </Field>
          <Field label="Categoria financeira" className="col-md-6" error={errors.financialCategoryId}>
            {select(
              "financialCategoryId",
              options.financialCategories,
              "Vendas de Serviços",
              order?.financialCategory,
            )}
          </Field>
          <div className="col-12 mb-3">
            <div className="form-check form-check-inline">
              <input
                id="os-quote-mode"
                type="checkbox"
                className="form-check-input"
                checked={f.quoteMode}
                onChange={(e) => set("quoteMode", e.target.checked)}
              />
              <label className="form-check-label" htmlFor="os-quote-mode">
                Orçar (valores a aprovar pelo cliente)
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input
                id="os-quoted"
                type="checkbox"
                className="form-check-input"
                checked={f.quoted}
                onChange={(e) => set("quoted", e.target.checked)}
              />
              <label className="form-check-label" htmlFor="os-quoted">
                Orçado
              </label>
            </div>
          </div>
        </div>
      </FormSection>

      <FormSection
        title="Laudo, garantia e observações"
        defaultOpen={
          !!(order?.serviceNotes || order?.technicianInfo || order?.warranty || order?.internalNotes)
        }
      >
        <div className="row">
          <Field label="Observações do serviço" className="col-12" error={errors.serviceNotes}>
            <textarea
              rows={2}
              maxLength={5000}
              value={f.serviceNotes}
              onChange={(e) => set("serviceNotes", e.target.value)}
            />
          </Field>
          <Field label="Laudo técnico" className="col-12" error={errors.technicianInfo}>
            <textarea
              rows={2}
              maxLength={5000}
              value={f.technicianInfo}
              onChange={(e) => set("technicianInfo", e.target.value)}
            />
          </Field>
          <Field label="Garantia" className="col-md-4" hint="Ex.: 90 dias" error={errors.warranty}>
            <input value={f.warranty} maxLength={120} onChange={(e) => set("warranty", e.target.value)} />
          </Field>
          <Field label="Condições da garantia" className="col-md-8" error={errors.warrantyInfo}>
            <input
              value={f.warrantyInfo}
              maxLength={5000}
              onChange={(e) => set("warrantyInfo", e.target.value)}
            />
          </Field>
          <Field
            label="Observações internas"
            className="col-12"
            hint="Não saem no PDF."
            error={errors.internalNotes}
          >
            <textarea
              rows={2}
              maxLength={5000}
              value={f.internalNotes}
              onChange={(e) => set("internalNotes", e.target.value)}
            />
          </Field>
        </div>
      </FormSection>
    </FormPage>
  );
}
